'use client'

import { ReactNode, useEffect, useState, useCallback } from 'react';
import styles from '../styles/Modal.module.css';
import { createPortal } from 'react-dom';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  currentStep?: number;
  totalSteps?: number;
}

export const Modal = ({ isOpen, onClose, title, children, currentStep, totalSteps }: ModalProps) => {
  const [mounted, setMounted] = useState(false);

  // Memoize the stopPropagation handler to avoid creating a new function on each render
  const handleContentClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  useEffect(() => {
    setMounted(true);
    
    // Add/remove class to body when modal is open/closed for scroll control via CSS
    if (isOpen) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }

    // Handle escape key press
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEsc);
    
    // Cleanup function
    return () => {
      document.body.classList.remove('modal-open');
      window.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen, onClose]);

  // Calculate progress percentage
  const progressPercentage = (totalSteps && currentStep && totalSteps > 0) 
    ? Math.min(((currentStep -1) / (totalSteps - 1)) * 100, 100) // Calculate progress based on completing steps
    : 0;

  // Style for the header with the custom property
  const headerStyle = {
    '--progress-width': `${progressPercentage}%`
  } as React.CSSProperties;

  if (!isOpen || !mounted) return null;

  // Use portal to render modal at the root level of the DOM
  return createPortal(
    <div className={styles.modalOverlay} onClick={onClose}>
      <div 
        className={styles.modalContent} 
        onClick={handleContentClick} // Use memoized callback
        data-lenis-prevent
      >
        <button className={styles.closeButton} onClick={onClose}>×</button>
        
        {title && (
          <div className={styles.modalHeader} style={headerStyle}>
            <h2>{title}</h2>
          </div>
        )}
        
        <div className={styles.modalBody}>
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
}; 